import React, { useMemo } from "react";
import "./TimeframeChart.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from "recharts";

/* 🔥 Custom tooltip that shows ALL coins, sorted by value */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;

  // Sort highest → lowest
  const sorted = [...payload].sort((a, b) => b.value - a.value);

  return (
    <div
      style={{
        background: "rgba(0,0,0,0.75)",
        borderRadius: "6px",
        padding: "6px 10px",
        color: "#fff"
      }}
    >
      <div style={{ marginBottom: 4, opacity: 0.8 }}>
        {label?.slice(0, 5)} {/* HH:mm */}
      </div>

      {sorted.map((entry) => (
        <div key={entry.dataKey} style={{ marginBottom: 2 }}>
          <span style={{ opacity: 0.8 }}>{entry.dataKey}</span>
          <span style={{ float: "right" }}>
            {entry.value.toFixed(2)}%
          </span>
        </div>
      ))}
    </div>
  );
}

function CustomLegend({ coins, selectedTimeframe }) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: "12px",
        marginTop: "8px",
        padding: "4px 0"
      }}
    >
      {coins.map((coin) => {
        const rawSpark = coin[`spark${selectedTimeframe}`] || [];
        const color = trendAwareColor(coin.symbol, rawSpark);

        return (
          <div key={coin.symbol} style={{ display: "flex", alignItems: "center" }}>
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: color,
                marginRight: 6
              }}
            />
            <span style={{ fontSize: 12, color: "var(--text)" }}>
              {coin.symbol}
            </span>
          </div>
        );
      })}
    </div>
  );
}



/* Deterministic base colour generator */
function baseColorForCoin(symbol) {
  let hash = 0;
  for (let i = 0; i < symbol.length; i++) {
    hash = symbol.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
}

/* Extract numeric sparkline values (NEW helper) */
function extractValues(spark) {
  if (!Array.isArray(spark)) return [];
  return spark.map(p => (typeof p === "number" ? p : p.v));
}

/* Trend-aware hue shift */
function trendAwareColor(symbol, spark) {
  const values = extractValues(spark);
  let hue = baseColorForCoin(symbol);

  if (values.length > 1) {
    const diff = values[values.length - 1] - values[0];
    if (diff > 0) hue += 10;
    else if (diff < 0) hue -= 10;
  }

  hue = (hue + 360) % 360;
  return `hsl(${hue}, 70%, 55%)`;
}

/* Convert sparkline to percent-change series */
function formatPercentData(coins, timeframe) {
  if (!coins || coins.length === 0) return [];

  const series = coins.map((coin) => {
    const rawSpark = coin[`spark${timeframe}`] || [];
    const spark = extractValues(rawSpark);
    const timestamps = rawSpark.map(p => (typeof p === "number" ? null : p.t));

    if (spark.length < 2) {
      return { symbol: coin.symbol, values: [], timestamps: [] };
    }

    const first = spark[0];
    const pctValues = spark.map(v => ((v - first) / first) * 100);

    return {
      symbol: coin.symbol,
      values: pctValues,
      timestamps
    };
  });

  if (series.length === 0 || series[0].values.length === 0) return [];

  const length = series[0].values.length;

  return Array.from({ length }, (_, i) => {
    const row = { t: series[0].timestamps[i] || "" };
    series.forEach((coin) => {
      row[coin.symbol] = coin.values[i];
    });
    return row;
  });
}

export default function TimeframeChart({ coins, height, selectedTimeframe }) {
  const data = useMemo(
    () => formatPercentData(coins, selectedTimeframe),
    [coins, selectedTimeframe]
  );

  if (!coins || coins.length === 0) return null;

  return (
    <div className="timeframe-chart">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={data}
          margin={{ top: 4, bottom: 4, left: 4, right: 4 }}
        >
          <XAxis
            dataKey="t"
            tickFormatter={(t) => t?.slice(0, 5)}   // HH:mm
            tick={{ fill: "var(--text)", fontSize: 10 }}
            interval="preserveStartEnd"
            minTickGap={20}
          />

          <YAxis
            domain={['dataMin', 'dataMax']}
            allowDataOverflow={true}
            tickFormatter={(v) => `${v.toFixed(0)}%`}
            tick={{ fill: "var(--text)", fontSize: 11 }}
            width={32}
          />

          <ReferenceLine
            y={0}
            stroke="var(--border)"
            strokeOpacity={0.35}
            strokeWidth={1}
          />

          <Tooltip content={<CustomTooltip />} />


          <defs>
            {coins.map((coin) => {
              const rawSpark = coin[`spark${selectedTimeframe}`] || [];
              const base = trendAwareColor(coin.symbol, rawSpark);

              return (
                <linearGradient
                  key={coin.symbol}
                  id={`grad-${coin.symbol}`}
                  x1="0"
                  x2="1"
                  y1="0"
                  y2="0"
                >
                  <stop offset="0%" stopColor={base} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={base} stopOpacity={1} />
                </linearGradient>
              );
            })}
          </defs>

          {coins.map((coin) => (
            <Line
              key={coin.symbol}
              type="monotone"
              dataKey={coin.symbol}
              stroke={`url(#grad-${coin.symbol})`}
              strokeWidth={1.8}
              strokeOpacity={0.95}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <CustomLegend coins={coins} selectedTimeframe={selectedTimeframe} />
    </div>
  );
}
